import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, 
  FileText, Calendar, Tag, Image, Loader2, ExternalLink,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/api';

const AdminBlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    excerpt: '',
    cover_image: '',
    tags: '',
    meta_description: '',
    meta_keywords: '',
    is_published: false
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/blog/posts`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      subtitle: '',
      content: '',
      excerpt: '',
      cover_image: '',
      tags: '',
      meta_description: '',
      meta_keywords: '',
      is_published: false
    });
    setShowEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      subtitle: post.subtitle || '',
      content: post.content,
      excerpt: post.excerpt,
      cover_image: post.cover_image || '',
      tags: post.tags?.join(', ') || '',
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords?.join(', ') || '',
      is_published: post.is_published
    });
    setShowEditor(true);
  };

  const handleSavePost = async () => {
    if (!formData.title || !formData.content || !formData.excerpt) {
      toast.error('Preencha título, conteúdo e resumo');
      return;
    }

    setSaving(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        meta_keywords: formData.meta_keywords.split(',').map(t => t.trim()).filter(t => t)
      };

      const url = editingPost 
        ? `${BACKEND_URL}/api/admin/blog/posts/${editingPost.post_id}`
        : `${BACKEND_URL}/api/admin/blog/posts`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingPost ? 'Post atualizado!' : 'Post criado!');
        setShowEditor(false);
        loadPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar post');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erro ao salvar post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Tem certeza que deseja excluir este post?')) return;

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (response.ok) {
        toast.success('Post excluído');
        loadPosts();
      } else {
        toast.error('Erro ao excluir post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erro ao excluir post');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/blog/posts/${post.post_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_published: !post.is_published })
      });

      if (response.ok) {
        toast.success(post.is_published ? 'Post despublicado' : 'Post publicado!');
        loadPosts();
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Erro ao alterar status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciar Blog</h2>
          <p className="text-gray-400 text-sm">{posts.length} posts no total</p>
        </div>
        <Button
          className="bg-[#00FF94] text-black hover:bg-[#00CC76] font-bold"
          onClick={handleNewPost}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Post
        </Button>
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <div className="text-center py-12 bg-[#0A0A0A] border border-white/10 rounded-xl">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">Nenhum post ainda</h3>
            <p className="text-gray-400 mb-4">Crie seu primeiro post para o blog</p>
            <Button
              className="bg-[#00FF94] text-black hover:bg-[#00CC76]"
              onClick={handleNewPost}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Post
            </Button>
          </div>
        ) : (
          posts.map((post) => (
            <motion.div
              key={post.post_id}
              className="bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white truncate">{post.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        post.is_published 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {post.is_published ? 'Publicado' : 'Rascunho'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-1">{post.excerpt}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.published_at || post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.view_count} views
                      </span>
                      {post.tags?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {post.tags.slice(0, 2).join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-white"
                      onClick={() => handleTogglePublish(post)}
                      title={post.is_published ? 'Despublicar' : 'Publicar'}
                    >
                      {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-white"
                      onClick={() => handleEditPost(post)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-red-400"
                      onClick={() => handleDeletePost(post.post_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {post.is_published && (
                      <a
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-[#00FF94] transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPost ? 'Editar Post' : 'Novo Post'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <Label className="text-gray-300">Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Título do post"
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            {/* Subtitle */}
            <div>
              <Label className="text-gray-300">Subtítulo</Label>
              <Input
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="Subtítulo opcional"
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            {/* Excerpt */}
            <div>
              <Label className="text-gray-300">Resumo * (aparece nos cards)</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Breve descrição do post (max 200 caracteres)"
                className="bg-black/50 border-white/10 text-white mt-1 h-20"
                maxLength={200}
              />
            </div>

            {/* Content */}
            <div>
              <Label className="text-gray-300">Conteúdo * (Markdown suportado)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Conteúdo do post em Markdown..."
                className="bg-black/50 border-white/10 text-white mt-1 h-64 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use **negrito**, *itálico*, ## títulos, - listas, etc.
              </p>
            </div>

            {/* Cover Image */}
            <div>
              <Label className="text-gray-300">URL da Imagem de Capa</Label>
              <Input
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                placeholder="https://exemplo.com/imagem.jpg"
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            {/* Tags */}
            <div>
              <Label className="text-gray-300">Tags (separadas por vírgula)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="finanças, dívidas, economia"
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            {/* SEO */}
            <div className="pt-4 border-t border-white/10">
              <h4 className="text-sm font-medium text-[#00FF94] mb-3">SEO</h4>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-300">Meta Description</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Descrição para o Google (max 160 caracteres)"
                    className="bg-black/50 border-white/10 text-white mt-1 h-20"
                    maxLength={160}
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Palavras-chave (separadas por vírgula)</Label>
                  <Input
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                    placeholder="como evitar dívidas, organizar finanças"
                    className="bg-black/50 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Publish Toggle */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-black/50 text-[#00FF94] focus:ring-[#00FF94]"
                />
                <Label htmlFor="is_published" className="text-white cursor-pointer">
                  Publicar imediatamente
                </Label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                className="text-gray-400"
                onClick={() => setShowEditor(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] font-bold"
                onClick={handleSavePost}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingPost ? 'Atualizar' : 'Criar'} Post
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogManager;
